/**
 * IntakeWizardPage — /requests/new and /requests/new/:sessionId
 *
 * New-request wizard. Walks the user through a conversation-style
 * clarification flow, presents an editable preview card, checks for
 * duplicates, then pushes the confirmed requirement to Notion.
 *
 * State machine (Phase B):
 *   idle → starting → answering → preview → deduping → confirming → done
 *   (rehydrating / rehydrateError for mount restore from URL param / localStorage)
 *
 * Managed entirely in useIntakeWizard; this page is a pure renderer.
 * Errors are per-step inline (no terminal global error screen).
 */

import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircleOutline as CheckIcon,
  DeleteOutline as DeleteIcon,
  LightbulbOutlined as LightbulbIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { AttachmentDropzone, type AttachmentDropzoneProps } from '@/components/AttachmentDropzone';
import { AttachmentList } from '@/components/AttachmentList';
import { itemFromFile, type AttachmentItem } from '@/hooks/useAttachmentUploads';
import { useIntakeWizard } from '@/hooks/useIntakeWizard';
import { useBuddyNarration } from '@/hooks/useBuddyNarration';
import { ResumeDraftBanner } from '@/pages/intake/ResumeDraftBanner';
import {
  DETECT_PROJECTS,
  type DetectProject,
  type IntakeAnswerItem,
  type IntakeQuestion,
  type RequirementBucket,
  type RequirementPreview,
  type RequirementSize,
  type RequirementType,
} from '@/types/intake';

const REQUIREMENT_TYPES: RequirementType[] = ['Bug', 'Feature', 'Enhancement', 'Tech', 'Adhoc'];
const SEVERITIES = ['P0', 'P1', 'P2', 'P3'] as const;
/** Display labels for the severity select in the preview card. */
const SEVERITY_LABELS: Record<string, string> = {
  P0: 'P0 Fix Now',
  P1: 'P1 Urgent',
  P2: 'P2 Normal',
  P3: 'P3 Low',
};
// Non-nullable arrays for option rendering (the null case is rendered as "—").
const SIZES: NonNullable<RequirementSize>[] = ['S', 'M', 'L'];
const BUCKETS: NonNullable<RequirementBucket>[] = ['Business', 'Engineering Health'];

/**
 * Canonical 17-option category list. Sourced from the wizard's category question
 * when available; this constant is the fallback for the preview-card editor when
 * the questions are no longer in scope.
 */
export const CATEGORY_OPTIONS = [
  'New homepage',
  'Arcgis',
  'Client Report',
  'Infrastructure',
  'AI',
  'Data Collection',
  'UX',
  'Info tab',
  'Data Analysis',
  'Chat',
  'Flight Planning',
  'Substation',
  'User Onboarding',
  'Engineering',
  'Image pipeline',
  'Mobile',
  'Performance',
] as const;

const OTHER_SENTINEL = '__other__';

/**
 * Per-question answer state. The "Other" free text is kept SEPARATE from the
 * selection so typing in the Other field only mutates `otherText` and never
 * the selection — otherwise overwriting `selected` flips the `showOther`
 * condition false and unmounts the input mid-keystroke (focus loss). Mirrors
 * the `{ selected, otherText }` shape proven in `components/ClarificationPanel`.
 */
interface IntakeAnswer {
  /** single-select: chosen option, OTHER_SENTINEL, or null. free-text: the typed text. */
  selected: string | null;
  /** multi-select: chosen options. */
  selectedMulti: string[];
  /** free text for a single-select "Other…" choice. */
  otherText: string;
}

const EMPTY_ANSWER: IntakeAnswer = { selected: null, selectedMulti: [], otherText: '' };

/**
 * Shared style for option buttons. Single-select and multi-select use the
 * SAME button styling (full-width, left-aligned, primary-colored) so the two
 * question types look identical — multi-select previously rendered with MUI
 * ToggleButtons whose default text is white, clashing with the orange
 * outlined buttons of single-select.
 */
const OPTION_BUTTON_SX = {
  textTransform: 'none',
  justifyContent: 'flex-start',
  textAlign: 'left',
  width: '100%',
  minHeight: 44,
  whiteSpace: 'normal',
  lineHeight: 1.4,
  py: 1,
} as const;

/** Resolve a structured answer to the wire string the backend expects. */
function resolveAnswer(q: IntakeQuestion, a: IntakeAnswer | undefined): string {
  if (!a) return '';
  if (q.question_type === 'single-select') {
    return a.selected === OTHER_SENTINEL ? a.otherText : (a.selected ?? '');
  }
  if (q.question_type === 'multi-select') {
    // The "Other…" toggle is carried as a sentinel in selectedMulti; swap it
    // for the typed free text (dropped when blank) before joining.
    return a.selectedMulti
      .map((v) => (v === OTHER_SENTINEL ? a.otherText.trim() : v))
      .filter(Boolean)
      .join('; ');
  }
  // free-text
  return a.selected ?? '';
}

/**
 * Project multi-select — the DetectOS product(s) a request targets. Toggle
 * chips (one or both). Shared by the step-1 form and the preview card.
 */
interface ProjectPickerProps {
  value: DetectProject[];
  onChange: (next: DetectProject[]) => void;
  disabled?: boolean;
  label?: string;
}

function ProjectPicker({ value, onChange, disabled, label }: ProjectPickerProps): ReactElement {
  const toggle = (p: DetectProject): void =>
    onChange(value.includes(p) ? value.filter((v) => v !== p) : [...value, p]);

  return (
    <FormControl component="fieldset" disabled={disabled} sx={{ width: '100%' }}>
      <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.9rem' }}>
        {label ?? 'Which product(s)?'}
      </FormLabel>
      <Stack direction="row" spacing={1} role="group" aria-label="Target project">
        {DETECT_PROJECTS.map((p) => {
          const active = value.includes(p);
          return (
            <Button
              key={p}
              size="small"
              variant={active ? 'contained' : 'outlined'}
              onClick={() => toggle(p)}
              disabled={disabled}
              aria-pressed={active}
              sx={{ textTransform: 'none', minHeight: 44 }}
            >
              {p}
            </Button>
          );
        })}
      </Stack>
    </FormControl>
  );
}

/**
 * The original request + target project(s), pinned to the top of the wizard so
 * it stays visible the whole way through (questions → preview → submit), even
 * when the content below scrolls.
 */
function RequestBanner({
  initialRequest,
  projects,
}: {
  initialRequest: string;
  projects: string[];
}): ReactElement {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        mb: 3,
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        Your request
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
        {initialRequest}
      </Typography>
      {projects.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          {projects.map((p) => (
            <Chip key={p} label={p} size="small" variant="outlined" color="primary" />
          ))}
        </Stack>
      )}
    </Box>
  );
}

/** Entry point — mounts the wizard page. */
export function IntakeWizardPage(): ReactElement {
  const wizard = useIntakeWizard();
  const { state, restoredAnswers } = wizard;
  const params = useParams<{ sessionId?: string }>();
  const hasSessionParam = Boolean(params.sessionId);

  // Wire the buddy to the wizard state so it walks + talks during LLM steps.
  useBuddyNarration(state, state.session?.id);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'primary.main',
          }}
        >
          new request
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Capture a new product requirement.
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Link component={RouterLink} to="/requests" variant="body2" underline="hover">
          ← Back to requests
        </Link>
      </Stack>

      {/* Rehydrating — skeleton while we fetch the session. */}
      {state.status === 'rehydrating' && (
        <Stack spacing={2} role="status" aria-label="Loading session">
          <Skeleton variant="rounded" height={64} />
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={44} width={140} />
        </Stack>
      )}

      {/* Rehydrate error — GET failed after retries. */}
      {state.status === 'rehydrateError' && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 3 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" color="inherit" onClick={() => void wizard.retry()}>
                Retry
              </Button>
              <Button size="small" color="inherit" onClick={wizard.reset}>
                Start fresh
              </Button>
            </Stack>
          }
        >
          {state.rehydrateError ?? 'Failed to reload your session.'}
        </Alert>
      )}

      {/* Inline per-step error — does NOT replace the active screen. */}
      {state.error && state.status !== 'rehydrating' && state.status !== 'rehydrateError' && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              {state.error.retriable && (
                <Button size="small" color="inherit" onClick={() => void wizard.retry()}>
                  Retry
                </Button>
              )}
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                onClick={wizard.reset}
              >
                Start over
              </Button>
            </Stack>
          }
        >
          {state.error.message}
        </Alert>
      )}

      {/* Auto-resume banner — shown only on bare /requests/new (no session param)
          and only when idle (not already rehydrating / in-flight). */}
      {!hasSessionParam && state.status === 'idle' && (
        <ResumeDraftBanner />
      )}

      {/* Sticky request banner — visible at every stage once a session exists
          (questions, preview, dedupe, confirming, done). */}
      {state.session && state.status !== 'rehydrating' && (
        <RequestBanner
          initialRequest={state.session.initial_request}
          projects={state.session.projects}
        />
      )}

      {(state.status === 'idle' || state.status === 'starting') && (
        <RequestForm
          loading={state.status === 'starting'}
          onSubmit={wizard.start}
        />
      )}

      {state.status === 'answering' && state.session && (
        <QuestionRound
          // Remount per round so answer state resets + new defaults seed cleanly.
          // Key on the question-set identity (not just the round number) so a new set
          // of questions ALWAYS remounts and clears the per-index answers — otherwise a
          // prior round's answer (e.g. Type) could bleed into the next round's field.
          key={`round-${state.session.round}-${(state.session.questions ?? [])
            .map((q) => q.field ?? q.text)
            .join('|')}`}
          session={state.session}
          restoredAnswers={restoredAnswers}
          onSubmit={wizard.submitAnswers}
          onFinalize={wizard.finalizeNow}
          onAnswersChange={wizard.onAnswersChange}
          onDiscard={wizard.discardCurrent}
          attachments={wizard.attachments}
          onAddAttachments={wizard.addAttachments}
          onRemoveAttachment={wizard.removeAttachment}
          onRetryAttachment={wizard.retryAttachment}
          coverageNote={state.coverageNote}
          stepRunning={state.stepRunning}
          hasError={state.error !== null}
        />
      )}

      {state.status === 'preview' && state.session?.derived && (
        <PreviewCard
          derived={state.session.derived}
          sessionId={state.session.id}
          projects={state.session.projects as DetectProject[]}
          sessionQuestions={state.session.questions ?? []}
          onUpdate={wizard.updatePreview}
          onProjectsChange={wizard.updateProjects}
          onProceed={wizard.submit}
          onBack={wizard.reset}
          onDiscard={wizard.discardCurrent}
          attachments={wizard.attachments}
          onAddAttachments={wizard.addAttachments}
          onRemoveAttachment={wizard.removeAttachment}
          onRetryAttachment={wizard.retryAttachment}
        />
      )}

      {state.status === 'deduping' && (
        <DedupePrompt
          candidates={state.dedupeCandidates}
          onConfirm={wizard.confirm}
          onBack={wizard.backToPreview}
        />
      )}

      {state.status === 'confirming' && (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}
          role="status"
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Creating requirement in Notion…
          </Typography>
        </Box>
      )}

      {state.status === 'done' && state.confirmResult && (
        <SuccessCard
          notionUrl={state.confirmResult.notion_url}
          dedupedInto={state.confirmResult.deduped_into}
          onStartOver={wizard.reset}
        />
      )}

      {/* "Already submitted" terminal screen when rehydrating a confirmed session. */}
      {state.status === 'done' && !state.confirmResult && state.session && (
        <AlreadySubmittedCard
          session={state.session}
          onStartOver={wizard.reset}
        />
      )}

      {/* Once confirm is in flight or done, attachments are read-only (downloadable). */}
      {(state.status === 'confirming' || state.status === 'done') &&
        wizard.attachments.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Attachments
            </Typography>
            <AttachmentList items={wizard.attachments} readOnly />
          </Box>
        )}
    </Box>
  );
}

const hasUploadsInFlight = (items: AttachmentItem[]): boolean =>
  items.some((it) => it.status === 'uploading');

function UploadsPendingHint(): ReactElement {
  return (
    <Typography variant="body2" color="text.secondary" role="status"
      data-testid="uploads-pending-hint"
      sx={{ alignSelf: 'center' }}
    >
      Waiting for uploads to finish…
    </Typography>
  );
}

/** Labelled attachment control shared by the form, question rounds and preview. */
function AttachmentsSection(props: AttachmentDropzoneProps): ReactElement {
  return (
    <Box component="section" aria-labelledby="intake-attachments-heading">
      <Typography variant="subtitle1" id="intake-attachments-heading" sx={{ mb: 0.5 }}>
        Attachments{' '}
        <Typography component="span" variant="body2" color="text.secondary">
          (optional)
        </Typography>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Screenshots, specs or logs for whoever builds this.
      </Typography>
      <AttachmentDropzone {...props} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — free-text request form
// ---------------------------------------------------------------------------

interface RequestFormProps {
  loading: boolean;
  onSubmit: (req: string, projects: DetectProject[], files?: File[]) => Promise<void>;
}

interface QueuedFile {
  key: string;
  file: File;
}

let queuedFileCounter = 0;

function RequestForm({ loading, onSubmit }: RequestFormProps): ReactElement {
  const [value, setValue] = useState('');
  const [projects, setProjects] = useState<DetectProject[]>([]);
  // The session doesn't exist yet, so attachments queue locally and upload after create.
  const [queue, setQueue] = useState<QueuedFile[]>([]);

  const canSubmit = value.trim().length > 0 && projects.length > 0;

  const queueItems = useMemo(
    () => queue.map(({ key, file }) => itemFromFile(key, file, 'queued')),
    [queue],
  );

  const addToQueue = useCallback((files: File[]) => {
    setQueue((prev) => [
      ...prev,
      ...files.map((file) => ({ key: `queued-${queuedFileCounter++}`, file })),
    ]);
  }, []);

  const removeFromQueue = useCallback((key: string) => {
    setQueue((prev) => prev.filter((q) => q.key !== key));
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || projects.length === 0) return;
    void onSubmit(
      trimmed,
      projects,
      queue.map((q) => q.file),
    );
  }, [value, projects, queue, onSubmit]);

  return (
    <Stack spacing={2}>
      <TextField
        id="intake-request"
        label="What do you need?"
        placeholder="e.g. Engineers need a way to annotate investigations with root-cause tags so we can track recurring patterns over time."
        multiline
        minRows={4}
        fullWidth
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={loading}
        inputProps={{ 'aria-label': 'Initial requirement request' }}
      />
      <ProjectPicker value={projects} onChange={setProjects} disabled={loading} />
      <AttachmentsSection
        items={queueItems}
        onAddFiles={addToQueue}
        onRemove={removeFromQueue}
        disabled={loading}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !canSubmit}
          sx={{ minWidth: 140, minHeight: 44 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Start request'}
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — clarification question round
// ---------------------------------------------------------------------------

interface QuestionRoundProps {
  session: NonNullable<ReturnType<typeof useIntakeWizard>['state']['session']>;
  /** Pre-populated answers from localStorage if round matches (rehydration). */
  restoredAnswers: ReturnType<typeof useIntakeWizard>['restoredAnswers'];
  onSubmit: (answers: IntakeAnswerItem[], finalize?: boolean, idempotency_key?: string) => Promise<void>;
  /** Called when the user wants to jump straight to the preview draft ("Looks good — draft it"). */
  onFinalize: (answers: IntakeAnswerItem[], idempotency_key?: string) => Promise<void>;
  /** Called on every answer change so the hook can persist to localStorage (debounced). */
  onAnswersChange: (sessionId: string, state: import('@/hooks/useIntakeDraftStorage').DraftAnswerState) => void;
  /** Soft-discard the current session. */
  onDiscard: () => Promise<void>;
  attachments: AttachmentItem[];
  onAddAttachments: (files: File[]) => void;
  onRemoveAttachment: (key: string) => void;
  onRetryAttachment: (key: string) => void;
  /**
   * Phase C — live coverage progress note driven by coverage_update SSE events.
   * e.g. "4 covered · 3 remaining". Null when not streaming / no event yet.
   */
  coverageNote: string | null;
  /** Phase C — true while a streaming step is running (show skeleton cards). */
  stepRunning: boolean;
  /** True when the wizard is surfacing a step error above this component. */
  hasError: boolean;
}

function QuestionRound({
  session,
  restoredAnswers,
  onSubmit,
  onFinalize,
  onAnswersChange,
  onDiscard,
  attachments,
  onAddAttachments,
  onRemoveAttachment,
  onRetryAttachment,
  coverageNote,
  stepRunning,
  hasError,
}: QuestionRoundProps): ReactElement {
  const sessionQuestions = session.questions;
  const questions = sessionQuestions ?? [];

  // Stable idempotency key for this round — regenerated on remount (per-round).
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  // answers[idx] = structured answer for that question (see IntakeAnswer).
  // Seed priority: 1) localStorage restored answers (matching round), 2) question.default.
  const [answers, setAnswers] = useState<Record<number, IntakeAnswer>>(() => {
    // Prefer restored answers from localStorage when the round matches.
    if (restoredAnswers && restoredAnswers.round === session.round) {
      // Cast: the stored shape matches IntakeAnswer.
      return restoredAnswers.answers as Record<number, IntakeAnswer>;
    }
    const seed: Record<number, IntakeAnswer> = {};
    questions.forEach((q, idx) => {
      if (q.default) seed[idx] = { ...EMPTY_ANSWER, selected: q.default };
    });
    return seed;
  });
  const [submitting, setSubmitting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // Persist answers to localStorage on every change (debounced inside the hook).
  useEffect(() => {
    onAnswersChange(session.id, { round: session.round, answers });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]); // intentional — only fire on answer changes

  // Granular updaters merge into the existing entry so a change to one field
  // (e.g. typing in "Other") never clobbers the others (e.g. the selection).
  const onSelect = useCallback((idx: number, sel: string) => {
    setAnswers((prev) => ({ ...prev, [idx]: { ...(prev[idx] ?? EMPTY_ANSWER), selected: sel } }));
  }, []);
  const onSelectMulti = useCallback((idx: number, sel: string[]) => {
    setAnswers((prev) => ({ ...prev, [idx]: { ...(prev[idx] ?? EMPTY_ANSWER), selectedMulti: sel } }));
  }, []);
  const onOtherChange = useCallback((idx: number, text: string) => {
    setAnswers((prev) => ({ ...prev, [idx]: { ...(prev[idx] ?? EMPTY_ANSWER), otherText: text } }));
  }, []);

  // Optional questions (e.g. "Anything else?") never block Continue.
  const allAnswered = questions.every(
    (q, idx) => q.optional || resolveAnswer(q, answers[idx]).trim().length > 0,
  );

  const buildItems = useCallback((): IntakeAnswerItem[] => {
    const qs = sessionQuestions ?? [];
    return qs.map((q, idx) => ({
      question_index: idx,
      value: resolveAnswer(q, answers[idx]),
    }));
  }, [answers, sessionQuestions]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    await onSubmit(buildItems(), false, idempotencyKey);
    setSubmitting(false);
  }, [buildItems, onSubmit, idempotencyKey]);

  const handleFinalize = useCallback(async () => {
    setFinalizing(true);
    await onFinalize(buildItems(), idempotencyKey);
    setFinalizing(false);
  }, [buildItems, onFinalize, idempotencyKey]);

  const handleDiscard = useCallback(async () => {
    setDiscarding(true);
    await onDiscard();
    setDiscarding(false);
  }, [onDiscard]);

  // A surfaced step error means nothing is in flight any more, so the progress
  // spinner would contradict the error Alert the page renders above us. Keep
  // Discard: the Alert's "Start over" is client-only, so it leaves the row
  // in_progress and the draft banner offers this same stalled session back.
  if (hasError && questions.length === 0) {
    return (
      <Stack spacing={3} alignItems="flex-start">
        <Button
          variant="text"
          size="small"
          color="error"
          onClick={handleDiscard}
          disabled={discarding}
          sx={{ minHeight: 44, textTransform: 'none', color: 'text.disabled' }}
        >
          {discarding ? <CircularProgress size={16} color="inherit" /> : 'Discard'}
        </Button>
      </Stack>
    );
  }

  // Skeleton: show layout-shaped placeholders while questions are loading
  // (questions === null means the round is still in flight server-side,
  //  or the streaming step is running and we're awaiting question_ready).
  if (questions.length === 0 || stepRunning) {
    // Build the progress text: if we have a live coverage note from a
    // coverage_update SSE event, show it; otherwise fall back to the generic copy.
    const progressText = coverageNote
      ? `Narrowing it down… ${coverageNote}`
      : 'Sherlock is figuring out what it needs…';

    return (
      <Stack spacing={3} role="status" aria-label="Loading questions">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={16} thickness={5} sx={{ color: 'primary.main', flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary" aria-live="polite">
            {progressText}
          </Typography>
        </Box>
        {/* Layout-shaped skeleton cards — 3 question placeholders */}
        {[...Array(3)].map((_, i) => (
          <Stack key={i} spacing={1}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rounded" height={44} />
            <Skeleton variant="rounded" height={44} />
            <Skeleton variant="rounded" height={44} />
          </Stack>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Skeleton variant="rounded" height={44} width={140} />
        </Box>
      </Stack>
    );
  }

  const busy = submitting || finalizing || discarding;
  const uploading = hasUploadsInFlight(attachments);

  return (
    <Stack spacing={3}>
      {/* Open-ended progress indicator — no fixed step count implied.
          When streaming, show the live coverage note from coverage_update events. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={16} thickness={5} sx={{ color: 'primary.main', flexShrink: 0 }} />
        <Typography variant="body2" color="text.secondary" aria-live="polite">
          {coverageNote
            ? `Narrowing it down… ${coverageNote}`
            : `Sherlock is figuring out what it needs… (round ${session.round})`}
        </Typography>
      </Box>

      {questions.map((q, idx) => (
        <QuestionBlock
          key={`${session.round}-${idx}`}
          idx={idx}
          question={q}
          answer={answers[idx]}
          onSelect={(v) => onSelect(idx, v)}
          onSelectMulti={(v) => onSelectMulti(idx, v)}
          onOtherChange={(v) => onOtherChange(idx, v)}
          onSkip={() => {
            // Submit empty value for this optional question — clears any partial answer.
            if (q.question_type === 'multi-select') {
              onSelectMulti(idx, []);
            } else {
              onSelect(idx, '');
            }
          }}
          disabled={busy}
        />
      ))}

      <AttachmentsSection
        items={attachments}
        onAddFiles={onAddAttachments}
        onRemove={onRemoveAttachment}
        onRetry={onRetryAttachment}
        disabled={busy}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* "Looks good — draft it" — available from round 1 onwards so the user
              can jump to the preview at any point if they feel the grilling is done. */}
          <Button
            variant="text"
            size="small"
            startIcon={finalizing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
            onClick={handleFinalize}
            disabled={busy || uploading}
            sx={{ minHeight: 44, color: 'text.secondary', textTransform: 'none' }}
          >
            Looks good — draft it
          </Button>

          {/* Discard — secondary confirm-guarded action */}
          <Button
            variant="text"
            size="small"
            color="error"
            onClick={handleDiscard}
            disabled={busy}
            sx={{ minHeight: 44, textTransform: 'none', color: 'text.disabled' }}
          >
            {discarding ? <CircularProgress size={16} color="inherit" /> : 'Discard'}
          </Button>
        </Stack>

        {uploading && <UploadsPendingHint />}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={busy || !allAnswered}
          sx={{ minWidth: 140, minHeight: 44 }}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// QuestionBlock — renders one question based on question_type
// ---------------------------------------------------------------------------

interface QuestionBlockProps {
  idx: number;
  question: IntakeQuestion;
  answer: IntakeAnswer | undefined;
  onSelect: (val: string) => void;
  onSelectMulti: (vals: string[]) => void;
  onOtherChange: (text: string) => void;
  /** Called when the user taps "Not sure / Skip" on an optional question. */
  onSkip: () => void;
  disabled: boolean;
}

function QuestionBlock({
  idx,
  question,
  answer,
  onSelect,
  onSelectMulti,
  onOtherChange,
  onSkip,
  disabled,
}: QuestionBlockProps): ReactElement {
  const label = `${idx + 1}. ${question.text}`;
  // The single-vs-multi cue: both render as identical buttons now, so this
  // caption is the only signal of how many options can be chosen.
  const hint = question.question_type === 'multi-select' ? 'Select any that apply' : 'Select one';

  /** Shared "why we suggest this" rationale line — rendered under every question type. */
  const RationaleHint =
    question.rationale ? (
      <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 0.75 }}>
        <LightbulbIcon
          fontSize="small"
          sx={{ color: 'text.disabled', mt: '2px', flexShrink: 0, fontSize: '0.9rem' }}
        />
        <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.5 }}>
          {question.rationale}
        </Typography>
      </Stack>
    ) : null;

  /** "Not sure / Skip" — only shown for optional questions. */
  const SkipLink =
    question.optional ? (
      <Box sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="text"
          onClick={onSkip}
          disabled={disabled}
          sx={{
            minHeight: 44,
            color: 'text.secondary',
            textTransform: 'none',
            px: 0,
            fontSize: '0.8rem',
          }}
        >
          Not sure / Skip
        </Button>
      </Box>
    ) : null;

  if (question.question_type === 'free-text') {
    const current = answer?.selected ?? '';
    const recommendations = question.recommendations ?? [];
    const recommended = question.default ?? null;
    // Non-destructive: tapping a suggestion fills an empty box or appends a new
    // line; skip if it's already present. The text stays fully editable.
    const appendSuggestion = (suggestion: string): void => {
      if (current.includes(suggestion)) return;
      onSelect(current.trim() ? `${current.trim()}\n${suggestion}` : suggestion);
    };

    return (
      <Box>
        <TextField
          id={`q-${idx}`}
          label={label}
          value={current}
          onChange={(e) => onSelect(e.target.value)}
          disabled={disabled}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
        {RationaleHint}
        {recommendations.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              Suggestions — tap to add
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {recommendations.map((suggestion) => {
                const isRecommended = suggestion === recommended;
                return (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    onClick={() => appendSuggestion(suggestion)}
                    disabled={disabled}
                    clickable
                    variant={isRecommended ? 'filled' : 'outlined'}
                    color="primary"
                    icon={
                      isRecommended ? (
                        <AutoAwesomeIcon sx={{ fontSize: '0.85rem !important' }} />
                      ) : undefined
                    }
                    sx={{
                      height: 'auto',
                      minHeight: 44,
                      borderRadius: 1.5,
                      fontWeight: isRecommended ? 600 : undefined,
                      '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75, lineHeight: 1.4 },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        )}
        {SkipLink}
      </Box>
    );
  }

  if (question.question_type === 'single-select') {
    const selected = answer?.selected ?? null;
    const showOther = selected === OTHER_SENTINEL;
    const recommended = question.default ?? null;

    return (
      <FormControl component="fieldset" disabled={disabled} sx={{ width: '100%' }}>
        <FormLabel component="legend" sx={{ mb: 0.25, fontSize: '0.9rem' }}>
          {label}
        </FormLabel>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {hint}
        </Typography>
        {RationaleHint}
        <Stack spacing={1} role="radiogroup" aria-label={label} sx={{ mt: 1 }}>
          {question.options.map((opt) => {
            const active = selected === opt;
            const isRecommended = opt === recommended;
            return (
              <Button
                key={opt}
                size="small"
                variant={active ? 'contained' : 'outlined'}
                onClick={() => onSelect(opt)}
                disabled={disabled}
                role="radio"
                aria-checked={active}
                endIcon={
                  isRecommended ? (
                    <Chip
                      label="Recommended"
                      size="small"
                      color={active ? 'default' : 'primary'}
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        pointerEvents: 'none',
                        border: 'none',
                        bgcolor: active ? 'rgba(255,255,255,0.2)' : 'action.hover',
                      }}
                    />
                  ) : undefined
                }
                sx={OPTION_BUTTON_SX}
              >
                {opt}
              </Button>
            );
          })}
          <Button
            size="small"
            variant={showOther ? 'contained' : 'outlined'}
            onClick={() => onSelect(OTHER_SENTINEL)}
            disabled={disabled}
            role="radio"
            aria-checked={showOther}
            sx={{ ...OPTION_BUTTON_SX, fontStyle: 'italic' }}
          >
            Other…
          </Button>
          {showOther && (
            <TextField
              size="small"
              placeholder="describe briefly…"
              value={answer?.otherText ?? ''}
              onChange={(e) => onOtherChange(e.target.value)}
              disabled={disabled}
              fullWidth
              inputProps={{ 'aria-label': `Other answer for question ${idx + 1}` }}
            />
          )}
        </Stack>
        {SkipLink}
      </FormControl>
    );
  }

  // multi-select — same button styling as single-select (full-width, primary
  // color) for visual consistency, but click TOGGLES membership rather than
  // replacing. An "Other…" toggle reveals a free-text field; the Other text
  // lives in `otherText`, separate from the selection, so typing never
  // unmounts the field. Resolved to a "; "-joined string on submit.
  const selected = answer?.selectedMulti ?? [];
  const showOther = selected.includes(OTHER_SENTINEL);
  const recommended = question.default ?? null;

  const toggle = (value: string): void => {
    onSelectMulti(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  return (
    <FormControl component="fieldset" disabled={disabled} sx={{ width: '100%' }}>
      <FormLabel component="legend" sx={{ mb: 0.25, fontSize: '0.9rem' }}>
        {label}
      </FormLabel>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {hint}
      </Typography>
      {RationaleHint}
      <Stack spacing={1} role="group" aria-label={label} sx={{ mt: 1 }}>
        {question.options.map((opt) => {
          const active = selected.includes(opt);
          const isRecommended = opt === recommended;
          return (
            <Button
              key={opt}
              size="small"
              variant={active ? 'contained' : 'outlined'}
              onClick={() => toggle(opt)}
              disabled={disabled}
              aria-pressed={active}
              endIcon={
                isRecommended ? (
                  <Chip
                    label="Recommended"
                    size="small"
                    color={active ? 'default' : 'primary'}
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      pointerEvents: 'none',
                      border: 'none',
                      bgcolor: active ? 'rgba(255,255,255,0.2)' : 'action.hover',
                    }}
                  />
                ) : undefined
              }
              sx={OPTION_BUTTON_SX}
            >
              {opt}
            </Button>
          );
        })}
        <Button
          size="small"
          variant={showOther ? 'contained' : 'outlined'}
          onClick={() => toggle(OTHER_SENTINEL)}
          disabled={disabled}
          aria-pressed={showOther}
          sx={{ ...OPTION_BUTTON_SX, fontStyle: 'italic' }}
        >
          Other…
        </Button>
        {showOther && (
          <TextField
            size="small"
            placeholder="describe briefly…"
            value={answer?.otherText ?? ''}
            onChange={(e) => onOtherChange(e.target.value)}
            disabled={disabled}
            fullWidth
            inputProps={{ 'aria-label': `Other answer for question ${idx + 1}` }}
          />
        )}
      </Stack>
      {SkipLink}
    </FormControl>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — preview card (editable)
// ---------------------------------------------------------------------------

interface PreviewCardProps {
  derived: RequirementPreview;
  sessionId: string;
  projects: DetectProject[];
  /** Wizard questions from the session — used to source the category option list. */
  sessionQuestions: IntakeQuestion[];
  onUpdate: (patch: Partial<RequirementPreview>) => Promise<void>;
  onProjectsChange: (projects: DetectProject[]) => Promise<void>;
  onProceed: () => Promise<void>;
  onBack: () => void;
  onDiscard: () => Promise<void>;
  attachments: AttachmentItem[];
  onAddAttachments: (files: File[]) => void;
  onRemoveAttachment: (key: string) => void;
  onRetryAttachment: (key: string) => void;
}

function PreviewCard({
  derived,
  projects,
  sessionQuestions,
  onUpdate,
  onProjectsChange,
  onProceed,
  onBack,
  onDiscard,
  attachments,
  onAddAttachments,
  onRemoveAttachment,
  onRetryAttachment,
}: PreviewCardProps): ReactElement {
  const [local, setLocal] = useState<RequirementPreview>({ ...derived });
  const [localProjects, setLocalProjects] = useState<DetectProject[]>(projects);

  // Derive category options from the wizard questions when possible; fall back to
  // the hardcoded constant so the editor works when questions are no longer present.
  const categoryOptions: readonly string[] = (() => {
    const catQ = sessionQuestions.find((q) => q.field === 'category');
    return catQ && catQ.options.length > 0 ? catQ.options : CATEGORY_OPTIONS;
  })();
  const [saving, setSaving] = useState(false);
  const [patchError, setPatchError] = useState<string | null>(null);
  const [proceeding, setProceeding] = useState(false);
  const [newCriteria, setNewCriteria] = useState('');
  const [discarding, setDiscarding] = useState(false);
  const uploading = hasUploadsInFlight(attachments);

  const changeProjects = useCallback(
    (next: DetectProject[]) => {
      setLocalProjects(next);
      void onProjectsChange(next);
    },
    [onProjectsChange],
  );

  const applyPatch = useCallback(
    async (patch: Partial<RequirementPreview>) => {
      const prevLocal = local;
      setSaving(true);
      setPatchError(null);
      // Optimistic: already applied by the caller via setLocal.
      try {
        await onUpdate(patch);
      } catch {
        // Rollback to prev on rejection.
        setLocal(prevLocal);
        setPatchError("Couldn't save — Retry");
      } finally {
        setSaving(false);
      }
    },
    [local, onUpdate],
  );

  const handleProceed = useCallback(async () => {
    setProceeding(true);
    await onProceed();
    setProceeding(false);
  }, [onProceed]);

  const handleDiscard = useCallback(async () => {
    setDiscarding(true);
    await onDiscard();
    setDiscarding(false);
  }, [onDiscard]);

  const addCriteria = useCallback(() => {
    const trimmed = newCriteria.trim();
    if (!trimmed) return;
    const next = [...local.acceptance_criteria, trimmed];
    setNewCriteria('');
    void applyPatch({ acceptance_criteria: next });
  }, [newCriteria, local.acceptance_criteria, applyPatch]);

  const removeCriteria = useCallback(
    (idx: number) => {
      const next = local.acceptance_criteria.filter((_, i) => i !== idx);
      void applyPatch({ acceptance_criteria: next });
    },
    [local.acceptance_criteria, applyPatch],
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5">Review &amp; edit</Typography>
        {saving && <CircularProgress size={16} />}
      </Stack>

      {patchError && (
        <Alert
          severity="warning"
          role="alert"
          sx={{ py: 0.5 }}
          action={
            <Button size="small" color="inherit" onClick={() => setPatchError(null)}>
              Dismiss
            </Button>
          }
        >
          {patchError}
        </Alert>
      )}

      <TextField
        id="preview-title"
        label="Title"
        value={local.title}
        onChange={(e) => setLocal((p) => ({ ...p, title: e.target.value }))}
        onBlur={() => void applyPatch({ title: local.title })}
        fullWidth
        size="small"
      />

      <TextField
        id="preview-problem"
        label="Problem"
        value={local.problem}
        onChange={(e) => setLocal((p) => ({ ...p, problem: e.target.value }))}
        onBlur={() => void applyPatch({ problem: local.problem })}
        fullWidth
        size="small"
        multiline
        minRows={2}
      />

      <TextField
        id="preview-outcome"
        label="Expected outcome"
        value={local.expected_outcome ?? ''}
        onChange={(e) => setLocal((p) => ({ ...p, expected_outcome: e.target.value }))}
        onBlur={() => void applyPatch({ expected_outcome: local.expected_outcome })}
        fullWidth
        size="small"
        multiline
        minRows={2}
      />

      {/* Acceptance criteria list */}
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Acceptance criteria
        </Typography>
        <Stack spacing={1}>
          {local.acceptance_criteria.map((ac, idx) => (
            <Stack key={idx} direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {ac}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Remove criterion ${idx + 1}`}
                onClick={() => removeCriteria(idx)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Add criterion…"
              value={newCriteria}
              onChange={(e) => setNewCriteria(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCriteria();
                }
              }}
              sx={{ flex: 1 }}
              inputProps={{ 'aria-label': 'New acceptance criterion' }}
            />
            <IconButton
              size="small"
              aria-label="Add acceptance criterion"
              onClick={addCriteria}
              disabled={!newCriteria.trim()}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Divider />

      {/* Target product(s) — chosen at start, editable here. */}
      <ProjectPicker value={localProjects} onChange={changeProjects} disabled={saving} />

      {/* Type / Severity / Size */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <FormLabel sx={{ mb: 0.5, fontSize: '0.8rem' }}>Type</FormLabel>
          <Select
            value={local.type}
            onChange={(e) => {
              const t = e.target.value as RequirementType;
              setLocal((p) => ({ ...p, type: t }));
              void applyPatch({ type: t });
            }}
            inputProps={{ 'aria-label': 'Requirement type' }}
          >
            {REQUIREMENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Severity — shown for all types (not only Bug).
            Display uses the full label (e.g. "P2 Normal"); PATCH sends canonical P0-P3. */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <FormLabel sx={{ mb: 0.5, fontSize: '0.8rem' }}>Severity</FormLabel>
          <Select
            value={local.severity ?? ''}
            onChange={(e) => {
              // e.target.value is '' or a canonical key like 'P2'
              const s = (e.target.value || null) as RequirementPreview['severity'];
              setLocal((p) => ({ ...p, severity: s }));
              void applyPatch({ severity: s });
            }}
            inputProps={{ 'aria-label': 'Severity' }}
          >
            <MenuItem value="">—</MenuItem>
            {SEVERITIES.map((s) => (
              <MenuItem key={s} value={s}>
                {SEVERITY_LABELS[s]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <FormLabel sx={{ mb: 0.5, fontSize: '0.8rem' }}>Size</FormLabel>
          <Select
            value={local.size ?? ''}
            onChange={(e) => {
              const sz = (e.target.value || null) as RequirementSize;
              setLocal((p) => ({ ...p, size: sz }));
              void applyPatch({ size: sz });
            }}
            inputProps={{ 'aria-label': 'Requirement size' }}
          >
            <MenuItem value="">—</MenuItem>
            {SIZES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Bucket — LLM-derived single-select, editable. */}
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <FormLabel sx={{ mb: 0.5, fontSize: '0.8rem' }}>Bucket</FormLabel>
        <Select
          value={local.bucket ?? ''}
          onChange={(e) => {
            const b = (e.target.value || null) as RequirementBucket;
            setLocal((p) => ({ ...p, bucket: b }));
            void applyPatch({ bucket: b });
          }}
          inputProps={{ 'aria-label': 'Bucket' }}
        >
          <MenuItem value="">—</MenuItem>
          {BUCKETS.map((b) => (
            <MenuItem key={b} value={b}>
              {b}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Category — multi-select chip picker over the 17-option list. */}
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Category
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap role="group" aria-label="Category">
          {categoryOptions.map((opt) => {
            const selected = (local.category ?? []).includes(opt);
            return (
              <Chip
                key={opt}
                label={opt}
                onClick={() => {
                  const next = selected
                    ? (local.category ?? []).filter((c) => c !== opt)
                    : [...(local.category ?? []), opt];
                  setLocal((p) => ({ ...p, category: next }));
                  void applyPatch({ category: next });
                }}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                clickable
                aria-pressed={selected}
                sx={{ minHeight: 32 }}
              />
            );
          })}
        </Stack>
      </Box>

      {/* Client — free-text. */}
      <TextField
        size="small"
        label="Client"
        value={local.client ?? ''}
        onChange={(e) => setLocal((p) => ({ ...p, client: e.target.value }))}
        onBlur={() => void applyPatch({ client: local.client || null })}
        fullWidth
        inputProps={{ 'aria-label': 'Client' }}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          size="small"
          label="Why now"
          value={local.why_now ?? ''}
          onChange={(e) => setLocal((p) => ({ ...p, why_now: e.target.value }))}
          onBlur={() => void applyPatch({ why_now: local.why_now || null })}
          fullWidth
          inputProps={{ 'aria-label': 'Why now' }}
        />
        {/* Due date — MUI DatePicker; wire value is YYYY-MM-DD string (backend _parse_due_date). */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Due"
            value={local.due ? dayjs(local.due) : null}
            onChange={(d) => {
              // d is a Dayjs object or null from MUI x-date-pickers v5
              const dateStr = d ? (d as ReturnType<typeof dayjs>).format('YYYY-MM-DD') : '';
              setLocal((p) => ({ ...p, due: dateStr || null }));
              void applyPatch({ due: dateStr || null });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{ minWidth: 180 }}
                inputProps={{ ...params.inputProps, 'aria-label': 'Due date' }}
              />
            )}
          />
        </LocalizationProvider>
      </Stack>

      <AttachmentsSection
        items={attachments}
        onAddFiles={onAddAttachments}
        onRemove={onRemoveAttachment}
        onRetry={onRetryAttachment}
        disabled={proceeding || discarding}
      />

      <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
        <Button
          variant="contained"
          onClick={handleProceed}
          disabled={proceeding || discarding || uploading}
          sx={{ minHeight: 44, minWidth: 160 }}
        >
          {proceeding ? <CircularProgress size={20} color="inherit" /> : 'Submit request'}
        </Button>
        <Button
          variant="text"
          onClick={onBack}
          disabled={proceeding || discarding}
          sx={{ minHeight: 44 }}
        >
          Start over
        </Button>
        {uploading && <UploadsPendingHint />}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="text"
          color="error"
          onClick={handleDiscard}
          disabled={proceeding || discarding}
          sx={{ minHeight: 44, textTransform: 'none', color: 'text.disabled' }}
        >
          {discarding ? <CircularProgress size={16} color="inherit" /> : 'Discard draft'}
        </Button>
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — dedupe prompt
// ---------------------------------------------------------------------------

interface DedupePromptProps {
  candidates: ReturnType<typeof useIntakeWizard>['state']['dedupeCandidates'];
  onConfirm: (linkToRequirementId: string | null) => Promise<void>;
  onBack: () => void;
}

function DedupePrompt({ candidates, onConfirm, onBack }: DedupePromptProps): ReactElement {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = useCallback(
    async (id: string | null) => {
      setConfirming(true);
      await onConfirm(id);
      setConfirming(false);
    },
    [onConfirm],
  );

  if (candidates === null) {
    return (
      <Box role="status" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Checking for similar requirements…
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Similar requirements found</Typography>

      <Alert severity="warning">
        Found {candidates.length} similar requirement{candidates.length > 1 ? 's' : ''}. You can
        link to an existing one or submit this as a new separate requirement.
      </Alert>
      <Stack spacing={2}>
        {candidates.map((c) => (
              <Box
                key={c.requirement_id}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2">{c.title}</Typography>
                      <Chip
                        label={`${Math.round(c.similarity * 100)}% similar`}
                        size="small"
                        color={c.similarity > 0.85 ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {c.problem_preview}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {c.stage && <Chip label={c.stage} size="small" variant="outlined" />}
                      <Chip label={c.status} size="small" variant="outlined" />
                    </Stack>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => void handleConfirm(c.requirement_id)}
                    disabled={confirming}
                    sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
                  >
                    Link instead
                  </Button>
                </Stack>
              </Box>
        ))}
      </Stack>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          onClick={() => void handleConfirm(null)}
          disabled={confirming}
          sx={{ minHeight: 44, minWidth: 160 }}
        >
          {confirming ? <CircularProgress size={20} color="inherit" /> : 'Submit anyway'}
        </Button>
        <Button variant="text" onClick={onBack} disabled={confirming} sx={{ minHeight: 44 }}>
          Back to edit
        </Button>
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — success card
// ---------------------------------------------------------------------------

interface SuccessCardProps {
  notionUrl: string | null;
  dedupedInto: string | null;
  onStartOver: () => void;
}

function SuccessCard({ notionUrl, dedupedInto, onStartOver }: SuccessCardProps): ReactElement {
  const isDeduped = Boolean(dedupedInto);

  return (
    <Stack spacing={3} alignItems="center" sx={{ py: 6 }}>
      <CheckIcon sx={{ fontSize: 64, color: 'success.main' }} />
      <Typography variant="h5">
        {isDeduped ? 'Linked to existing requirement' : 'Requirement created'}
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        {isDeduped
          ? 'Your request was linked to an existing requirement in Notion.'
          : 'Your requirement has been added to Notion and is ready for grooming.'}
      </Typography>
      {notionUrl && (
        <Button
          variant="contained"
          href={notionUrl}
          target="_blank"
          rel="noopener noreferrer"
          component={Link}
          endIcon={<OpenInNewIcon />}
          sx={{ minHeight: 44 }}
        >
          View in Notion
        </Button>
      )}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="outlined" onClick={onStartOver} sx={{ minHeight: 44 }}>
          New request
        </Button>
        <Link component={RouterLink} to="/requests" variant="body2" underline="hover">
          Back to requests
        </Link>
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Already-submitted terminal card (rehydrated confirmed session)
// ---------------------------------------------------------------------------

interface AlreadySubmittedCardProps {
  session: ReturnType<typeof useIntakeWizard>['state']['session'];
  onStartOver: () => void;
}

function AlreadySubmittedCard({ session, onStartOver }: AlreadySubmittedCardProps): ReactElement {
  const notionUrl = session?.notion_task_id
    ? `https://notion.so/${session.notion_task_id.replace(/-/g, '')}`
    : null;
  return (
    <Stack spacing={3} alignItems="center" sx={{ py: 6 }}>
      <CheckIcon sx={{ fontSize: 64, color: 'success.main' }} />
      <Typography variant="h5">Already submitted</Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        This requirement was already confirmed and created in Notion.
      </Typography>
      {notionUrl && (
        <Button
          variant="contained"
          href={notionUrl}
          target="_blank"
          rel="noopener noreferrer"
          component={Link}
          endIcon={<OpenInNewIcon />}
          sx={{ minHeight: 44 }}
        >
          View in Notion
        </Button>
      )}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="outlined" onClick={onStartOver} sx={{ minHeight: 44 }}>
          New request
        </Button>
        <Link component={RouterLink} to="/requests" variant="body2" underline="hover">
          Back to requests
        </Link>
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton for suspense / lazy loading
// ---------------------------------------------------------------------------

export function IntakeWizardPageSkeleton(): ReactElement {
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Skeleton variant="text" width={180} height={40} sx={{ mb: 3 }} />
      <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={44} width={140} />
    </Box>
  );
}
